import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { FetchBaseQueryError, FetchBaseQueryMeta, createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { RootState } from '../store';
import { TupleCountCutterStates } from '../types/cutterState';

export const maximalServerResultsLength: number = 100;
export const minimalSearchStringLength: number = 3;

export interface SearchParameters {
    recentSearches: string[];
    expanded: boolean;
    favorites: string[];
}

const initialSearchState: SearchParameters = {
    recentSearches: ['Vector Auto iX6-86'],
    expanded: false,
    favorites: ['19FE6922']
}

export const searchSlice = createSlice({
    name: 'searchRoot',
    initialState: initialSearchState,
    reducers: {
        setSearch: (state, action: PayloadAction<string>) => {
            const newSearch = action.payload;
            if (!newSearch || newSearch.trim() === '') return;
            const filtered = state.recentSearches.filter(s => s !== newSearch);
            state.recentSearches = [newSearch, ...filtered].slice(0, 4);
        },
        setFavorite: (state, serialNumber: PayloadAction<string>) => {
            if (serialNumber.payload === '') { return state; }
            let clone = [...state.favorites];
            if (state.favorites.includes(serialNumber.payload)) {
                clone.splice(state.favorites.indexOf(serialNumber.payload), 1)
            } else {
                clone = [serialNumber.payload, ...state.favorites]
            }
            state.favorites = [...clone];
            return state;
        }
    }
})

const searchConfigs = {
    key: 'searchState',
    storage
};

export const cutterSearchApi = createApi({
    reducerPath: 'CutterSearchApi',
    tagTypes: ['CutterState'],
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.PUBLIC_URL}/api/CutterSearch/`,
        prepareHeaders: (headers, { getState }) => {
            const accessToken = (getState() as RootState).userStateReducer.accessToken;

            if (accessToken !== '') {
                headers.set('authorization', `Bearer ${accessToken}`)
            }

            return headers
        },
    }),
    endpoints: (build) => ({
        getCutterByString: build.query<TupleCountCutterStates, string>({
            query: (searchString: string) => `Find?searchString=${searchString}`,
            providesTags: ['CutterState'],
            transformErrorResponse: (failure: FetchBaseQueryError, meta: FetchBaseQueryMeta) => {
                let returnedError: string = 'An error occured while requesting cutter state';
                switch (failure.status) {
                    case "FETCH_ERROR":
                        break;
                    case "PARSING_ERROR":
                        break;
                    case "TIMEOUT_ERROR":
                        break;
                    case "CUSTOM_ERROR":
                        break;
                }
                returnedError = `${failure.status}  - ${returnedError}`;
                return returnedError;
            }
        })
    }),
})

export const persistedSearchReducer = persistReducer(searchConfigs, searchSlice.reducer);
export const { setSearch, setFavorite } = searchSlice.actions;

export const useGetCutterByStringQueryState = cutterSearchApi.endpoints.getCutterByString.useQueryState;
export const { useGetCutterByStringQuery } = cutterSearchApi;
